import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="legal-page">
      <section className="section">
        <div className="container">
          <div className="legal-content">
            <h1>Privacy Policy</h1>
            <p className="legal-effective-date"><strong>Effective Date:</strong> February 11, 2026</p>

            <p>
              Inferium Reports ("we," "our," or "us") operates the Inferium Reports mobile application
              and web platform (collectively, the "Service"). This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our Service.
            </p>

            <h2>1. Information We Collect</h2>

            <h3>Account Information</h3>
            <p>
              When your organization creates an account on your behalf, we collect:
            </p>
            <ul>
              <li>Full name</li>
              <li>Account username or email address</li>
              <li>Role within your organization (e.g., Guard, Manager, Admin)</li>
              <li>Password (stored in encrypted form)</li>
            </ul>

            <h3>Location Data</h3>
            <p>
              Our Service collects precise location data to support security guard operations, including:
            </p>
            <ul>
              <li><strong>Foreground location:</strong> Used for site check-ins, geofence verification, and attaching GPS coordinates to security reports.</li>
              <li><strong>Background location:</strong> Used during active guard shifts to track patrol routes and ensure site coverage. Background location is only collected while a guard is checked in to an active shift.</li>
            </ul>
            <p>
              Location data is essential to the core functionality of the Service. Guards are informed
              when location tracking is active, and tracking stops when a shift ends or the guard checks out.
            </p>

            <h3>Photos and Media</h3>
            <p>
              Guards may capture photos as part of security reports. These images are uploaded to our
              cloud storage provider and attached to the corresponding report. Photos may include
              metadata such as timestamp and location.
            </p>

            <h3>Phone Numbers</h3>
            <p>
              When a security company shares a report with a client or property manager via SMS,
              we collect and store the recipient's phone number. Phone numbers are provided by
              authorized administrators within the security company and are used solely to
              deliver report notifications.
            </p>

            <h3>Device Information</h3>
            <p>
              We may collect device-level information to support push notifications and app functionality, including:
            </p>
            <ul>
              <li>Device type and operating system version</li>
              <li>Push notification tokens (for delivering task and shift notifications)</li>
            </ul>

            <h3>Usage Data</h3>
            <p>
              We collect information about how the Service is used, including report submissions,
              check-in/check-out events, and task completions. This data is used to provide the
              Service and generate reports for your organization.
            </p>

            <h2>2. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, operate, and maintain the Service</li>
              <li>Verify guard presence at assigned security sites via geofencing</li>
              <li>Generate security reports for your organization</li>
              <li>Send push notifications for task reminders and shift updates</li>
              <li>Support offline report queuing and synchronization</li>
              <li>Improve and optimize the Service</li>
              <li>Respond to support requests</li>
              <li>Send one-time SMS notifications to deliver security report links to clients and property managers</li>
            </ul>

            <h2>3. How We Share Your Information</h2>
            <p>We may share your information in the following circumstances:</p>
            <ul>
              <li><strong>With your organization:</strong> Reports, location data, and activity logs are shared with authorized administrators and managers within your organization.</li>
              <li><strong>Service providers:</strong> We use third-party services to host and process data, including cloud storage (for photos), content delivery networks, and SMS delivery providers (including Twilio, Inc.) to send report notifications.</li>
              <li><strong>Legal requirements:</strong> We may disclose information if required by law, regulation, or legal process.</li>
            </ul>
            <p>We do not sell your personal information to third parties.</p>

            <h2>4. Data Storage and Security</h2>
            <p>
              Your data is transmitted over encrypted connections (HTTPS/TLS). Authentication tokens
              are stored securely on your device using platform-provided secure storage mechanisms.
              Photos are stored on secure cloud infrastructure. We implement industry-standard security
              measures to protect your data from unauthorized access.
            </p>

            <h2>5. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed
              to provide the Service. Report data is retained according to your organization's
              retention policies. When an account is deleted, associated personal data is removed
              in accordance with applicable law.
            </p>

            <h2>6. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict certain processing</li>
              <li>Data portability</li>
            </ul>
            <p>
              To exercise any of these rights, contact your organization's administrator or
              reach out to us at <a href="mailto:support@inferiumreports.com">support@inferiumreports.com</a>.
            </p>

            <h2>7. Children's Privacy</h2>
            <p>
              The Service is not intended for use by individuals under the age of 18. We do not
              knowingly collect personal information from children.
            </p>

            <h2>8. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes
              by posting the new Privacy Policy on this page and updating the effective date.
              Continued use of the Service after changes constitutes acceptance of the updated policy.
            </p>

            <h2>9. SMS Communications</h2>
            <p>
              When an authorized administrator shares a security report via SMS, the designated
              recipient will receive a one-time text message containing a link to view the report.
              SMS notifications are transactional and are only sent when explicitly initiated by
              an administrator on behalf of an existing business relationship.
            </p>
            <ul>
              <li><strong>Message frequency:</strong> One message per report shared. There is no recurring or subscription-based messaging.</li>
              <li><strong>Message and data rates may apply</strong> depending on your mobile carrier and plan.</li>
              <li><strong>To opt out:</strong> Reply <strong>STOP</strong> to any SMS message to stop receiving future messages. You will receive a confirmation and no further messages will be sent to your number.</li>
              <li><strong>For help:</strong> Reply <strong>HELP</strong> to any SMS message or contact us at <a href="mailto:support@inferiumreports.com">support@inferiumreports.com</a>.</li>
              <li>SMS delivery is powered by Twilio, Inc. Phone numbers are not shared with any other third parties for marketing purposes.</li>
            </ul>

            <h2>10. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <ul className="legal-contact-list">
              <li><strong>Email:</strong> <a href="mailto:support@inferiumreports.com">support@inferiumreports.com</a></li>
              <li><strong>Website:</strong> <a href="/contact">inferiumreports.com/contact</a></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
